import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { Appearance, CreationCatalog, Sex } from '../../api/types';
import { t } from '../../i18n/idioma';
import { nomeDaBarba, nomeDoCabelo } from '../../i18n/nomes';
import { aquecerCabelo, cabeloCss } from '../../look/cabelo';
import { peleCss } from '../../look/pele';
import { CORES_CRIACAO, PELES_CRIACAO, type CorDaPaleta } from '../../look/paletaCriacao';
import {
  carregarPersonalizacao,
  type EstiloDaTela, type Personalizacao, type Retangulo, type Visual
} from '../../look/personalizacao';
import { AvatarCriacao } from './AvatarCriacao';
import { IconeDado, IconeHomem, IconeMulher } from './icones';

interface Props {
  catalog: CreationCatalog;
  shardNome: string;
  nome: string;
  sex: Sex;
  value: Appearance;
  onNome: (nome: string) => void;
  onSex: (sex: Sex) => void;
  onChange: (next: Appearance) => void;
}

type Carga = Personalizacao | 'carregando' | 'falhou';

function escolhivel(e: EstiloDaTela): boolean {
  return e.id === 'none' || e.arte !== null;
}

function estilosDaTela(p: Personalizacao, catalog: CreationCatalog, sex: Sex, tipo: 'cabelo' | 'barba'): EstiloDaTela[] {
  const fonte = tipo === 'barba' ? p.barba : p.cabelo;
  const catalogo = tipo === 'barba'
    ? catalog.beards
    : sex === 'male' ? catalog.maleHair : catalog.femaleHair;
  const ids = new Set(catalogo.map(i => i.id));
  if (import.meta.env.DEV) {
    for (const e of fonte) {
      if (!ids.has(e.id)) console.warn('[personalizacao] id fora do catálogo', e.id);
    }
  }
  return fonte.filter(e => ids.has(e.id));
}

export function normalizar(p: Personalizacao, catalog: CreationCatalog, sex: Sex, appearance: Appearance): Appearance {
  const cabelos = estilosDaTela(p, catalog, sex, 'cabelo').filter(escolhivel);
  const hairStyle = cabelos.some(e => e.id === appearance.hairStyle)
    ? appearance.hairStyle
    : (cabelos[0]?.id ?? appearance.hairStyle);
  if (sex === 'female') {
    return { ...appearance, hairStyle, beardStyle: null, beardHue: null };
  }
  const barbas = estilosDaTela(p, catalog, 'male', 'barba');
  const beardStyle = appearance.beardStyle && barbas.some(e => e.id === appearance.beardStyle && escolhivel(e))
    ? appearance.beardStyle
    : 'none';
  const beardHue = beardStyle === 'none'
    ? null
    : (appearance.beardHue ?? appearance.hairHue);
  return { ...appearance, hairStyle, beardStyle, beardHue };
}

function nomeDaCor(lista: readonly CorDaPaleta[], hue: number): string {
  const c = lista.find(x => x.hue === hue);
  return c ? t(c.chave) : t('aparencia.tom', { n: hue });
}

function visualDe(sex: Sex, value: Appearance, hairStyle: string, beardStyle: string): Visual {
  return {
    sex,
    skinHue: value.skinHue,
    hairStyle,
    hairHue: value.hairHue,
    beardStyle,
    beardHue: value.beardHue ?? value.hairHue
  };
}

export function AparenciaCriacao({ catalog, shardNome, nome, sex, value, onNome, onSex, onChange }: Props) {
  const [p, setP] = useState<Carga>('carregando');
  const [mesma, setMesma] = useState(() => value.beardHue == null || value.beardHue === value.hairHue);
  const [prontoCabelo, setProntoCabelo] = useState(false);
  const [avatarFalhou, setAvatarFalhou] = useState(false);
  const normalizou = useRef(false);
  const pronto = typeof p === 'object' ? p : null;
  const nomeLimpo = nome.trim();
  const barba = value.beardStyle ?? 'none';

  useEffect(() => {
    let live = true;
    carregarPersonalizacao()
      .then(got => { if (live) setP(got); })
      .catch(() => { if (live) setP('falhou'); });
    aquecerCabelo().then(() => { if (live) setProntoCabelo(true); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!pronto || normalizou.current) return;
    normalizou.current = true;
    const next = normalizar(pronto, catalog, sex, value);
    if (next.hairStyle !== value.hairStyle || next.beardStyle !== value.beardStyle || next.beardHue !== value.beardHue) {
      onChange(next);
    }
  }, [pronto, catalog, sex, value, onChange]);

  const cabelos = pronto ? estilosDaTela(pronto, catalog, sex, 'cabelo') : [];
  const barbas = pronto ? estilosDaTela(pronto, catalog, 'male', 'barba') : [];
  const visual = visualDe(sex, value, value.hairStyle, sex === 'male' ? barba : 'none');
  const reserva = (id: string, tipo: 'cabelo' | 'barba') => {
    const lista = tipo === 'barba' ? catalog.beards : sex === 'male' ? catalog.maleHair : catalog.femaleHair;
    return lista.find(i => i.id === id)?.label;
  };

  const trocarSexo = (s: Sex) => {
    onSex(s);
    if (pronto) onChange(normalizar(pronto, catalog, s, value));
  };

  const trocarCorDoCabelo = (hue: number) => {
    const comBarba = sex === 'male' && barba !== 'none';
    onChange({ ...value, hairHue: hue, beardHue: mesma && comBarba ? hue : value.beardHue });
  };

  const trocarMesma = (v: boolean) => {
    setMesma(v);
    if (v) onChange({ ...value, beardHue: value.hairHue });
  };

  const sortear = () => {
    if (!pronto) return;
    const sorteia = <T,>(l: readonly T[]) => l[Math.floor(Math.random() * l.length)];
    const pelos = cabelos.filter(escolhivel).map(e => e.id);
    const pelosBarba = barbas.filter(escolhivel).map(e => e.id);
    if (!pelos.length) return;
    const hairHue = sorteia(CORES_CRIACAO).hue;
    const beardStyle = sex === 'male' && pelosBarba.length ? sorteia(pelosBarba) : null;
    onChange(normalizar(pronto, catalog, sex, {
      ...value,
      skinHue: sorteia(PELES_CRIACAO).hue,
      hairStyle: sorteia(pelos),
      hairHue,
      beardStyle,
      beardHue: beardStyle && beardStyle !== 'none' ? (mesma ? hairHue : sorteia(CORES_CRIACAO).hue) : null
    }));
  };

  const falhouAvatar = avatarFalhou && prontoCabelo && !!pronto;

  return (
    <div className="cr">
      <div className="cr-retrato">
        <div className="cr-quadro" role="img" aria-label={t('aparencia.oPersonagem')}>
          <AvatarCriacao className="cr-corpo" width={600} height={720}
            p={pronto} visual={visual} recorte={pronto?.quadro[sex] ?? null}
            onFalha={() => setAvatarFalhou(true)} />
          {falhouAvatar && <p className="cr-quadro__aviso">{t('aparencia.avatarFalhou')}</p>}
          <i className="cr-canto cr-canto--a" /><i className="cr-canto cr-canto--b" />
          <i className="cr-canto cr-canto--c" /><i className="cr-canto cr-canto--d" />
        </div>
        <div className="cr-placa">
          <b className={nomeLimpo ? undefined : 'is-vazio'}>{nomeLimpo || t('aparencia.semNome')}</b>
          <span>{shardNome}</span>
        </div>
        <button type="button" className="btn btn--ghost btn--tiny cr-sortear" onClick={sortear}>
          <IconeDado />{t('aparencia.sortear')}
        </button>
      </div>

      <div className="cr-opcoes">
        <label className="field">
          <span className="field__label">{t('comum.nome')}</span>
          <input value={nome} onChange={e => onNome(e.target.value)}
            maxLength={16} autoComplete="off" spellCheck={false} />
        </label>

        <Bloco titulo={t('aparencia.corpo')}>
          <div className="cr-seg" role="radiogroup" aria-label={t('aparencia.corpo')}>
            <button type="button" role="radio" aria-checked={sex === 'male'} onClick={() => trocarSexo('male')}>
              <IconeHomem />{t('personagem.homem')}
            </button>
            <button type="button" role="radio" aria-checked={sex === 'female'} onClick={() => trocarSexo('female')}>
              <IconeMulher />{t('personagem.mulher')}
            </button>
          </div>
        </Bloco>

        <Bloco titulo={t('personagem.pele')} valor={nomeDaCor(PELES_CRIACAO, value.skinHue)}>
          <Cores lista={PELES_CRIACAO} atual={value.skinHue} rotulo={t('personagem.pele')} css={peleCss}
            onPick={h => onChange({ ...value, skinHue: h })} />
        </Bloco>

        <Bloco titulo={t('personagem.cabelo')} valor={nomeDoCabelo(value.hairStyle, reserva(value.hairStyle, 'cabelo'))}>
          <Cartas tipo="cabelo" estilos={cabelos} atual={value.hairStyle} p={pronto} sex={sex} value={value}
            recorte={pronto?.cartaCabelo[sex] ?? null}
            rotulo={id => nomeDoCabelo(id, reserva(id, 'cabelo'))}
            onPick={id => onChange({ ...value, hairStyle: id })} />
        </Bloco>

        <Bloco titulo={t('personagem.corDoCabelo')} valor={nomeDaCor(CORES_CRIACAO, value.hairHue)}>
          <Cores lista={CORES_CRIACAO} atual={value.hairHue} rotulo={t('personagem.corDoCabelo')} css={cabeloCss}
            onPick={trocarCorDoCabelo} />
        </Bloco>

        {sex === 'male' && (
          <Bloco titulo={t('personagem.barba')} valor={nomeDaBarba(barba, reserva(barba, 'barba'))}>
            <Cartas tipo="barba" estilos={barbas} atual={barba} p={pronto} sex={sex} value={value}
              recorte={pronto?.cartaBarba.male ?? null}
              rotulo={id => nomeDaBarba(id, reserva(id, 'barba'))}
              onPick={id => onChange({
                ...value,
                beardStyle: id,
                beardHue: id === 'none' ? null : (mesma ? value.hairHue : (value.beardHue ?? value.hairHue))
              })} />
          </Bloco>
        )}

        {sex === 'male' && barba !== 'none' && (
          <div className="cr-bloco">
            <div className="cr-bloco__cab">
              <span className="field__label">{t('personagem.corDaBarba')}</span>
              <label className="cr-mesma">
                <input type="checkbox" checked={mesma} onChange={e => trocarMesma(e.target.checked)} />
                {' '}{t('aparencia.igualAoCabelo')}
              </label>
            </div>
            {!mesma && (
              <Cores lista={CORES_CRIACAO} atual={value.beardHue ?? value.hairHue}
                rotulo={t('personagem.corDaBarba')} css={cabeloCss}
                onPick={h => onChange({ ...value, beardHue: h })} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Bloco({ titulo, valor, children }: { titulo: string; valor?: string; children: ReactNode }) {
  return (
    <div className="cr-bloco">
      <div className="cr-bloco__cab">
        <span className="field__label">{titulo}</span>
        {valor && <span className="cr-bloco__val">{valor}</span>}
      </div>
      {children}
    </div>
  );
}

function Cores({ lista, atual, rotulo, css, onPick }: {
  lista: readonly CorDaPaleta[];
  atual: number;
  rotulo: string;
  css: (hue: number) => string;
  onPick: (hue: number) => void;
}) {
  return (
    <div className="cr-cores" role="radiogroup" aria-label={rotulo}>
      {lista.map(c => (
        <button key={c.hue} type="button" className="cr-cor" role="radio" aria-checked={c.hue === atual}
          aria-label={t(c.chave)} title={t('aparencia.corTitulo', { nome: t(c.chave), n: c.hue })}
          style={{ '--c': css(c.hue) } as CSSProperties} onClick={() => onPick(c.hue)} />
      ))}
    </div>
  );
}

function Cartas({ tipo, estilos, atual, p, sex, value, recorte, rotulo, onPick }: {
  tipo: 'cabelo' | 'barba';
  estilos: readonly EstiloDaTela[];
  atual: string;
  p: Personalizacao | null;
  sex: Sex;
  value: Appearance;
  recorte: Retangulo | null;
  rotulo: (id: string) => string;
  onPick: (id: string) => void;
}) {
  return (
    <div className="cr-cartas" role="radiogroup" aria-label={t(tipo === 'cabelo' ? 'personagem.cabelo' : 'personagem.barba')}
      style={{ '--colunas': colunasDasCartas(estilos.length) } as CSSProperties}>
      {estilos.map(e => {
        const liga = escolhivel(e);
        const hairStyle = tipo === 'cabelo' ? (liga ? e.id : 'none') : 'none';
        const beardStyle = tipo === 'barba' ? (liga ? e.id : 'none') : 'none';
        return (
          <button key={e.id} type="button" className="cr-carta" role="radio" aria-checked={e.id === atual}
            disabled={!liga} title={liga ? undefined : t('aparencia.semArteDica')}
            onClick={() => { if (liga) onPick(e.id); }}>
            <AvatarCriacao width={168} height={168} p={p} visual={visualDe(sex, value, hairStyle, beardStyle)} recorte={recorte} />
            <span>{rotulo(e.id)}</span>
            {!liga && <i className="cr-carta__sem">{t('aparencia.semArte')}</i>}
          </button>
        );
      })}
    </div>
  );
}

/** Até 6 estilos numa linha; com mais (os cabelos da UO são 9 ou 10), duas linhas, para a carta não espremer. */
function colunasDasCartas(n: number): number {
  return n <= 6 ? Math.max(4, n) : Math.ceil(n / 2);
}
